import { Component, EventEmitter, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { Country } from '@app/finxone-web-frontend/app/lib/dtos/countries.dto';
import { Currency } from '@app/finxone-web-frontend/app/lib/dtos/currencies.dto';
import { GetBeneficiaryResponse } from '@app/finxone-web-frontend/app/lib/services/account-service/account.type';
import { SupportedInstitution } from '@app/finxone-web-frontend/app/lib/services/savings-account-service/savings-account.type';
import { resolveAccountIdentifierFields } from '@app/finxone-web-frontend/app/lib/utils/account-identifier.utils';
import { addDashesToSortCode } from '@app/finxone-web-frontend/app/lib/utils/template/string.utils';
import {
  getCountryNameByCountryCode,
  getCurrenciesDataByKey,
  getCurrencyDataByKey,
} from '@app/finxone-web-frontend/app/lib/utils/utils';
import {
  AccountInfoType,
  AccountTypes,
  FileDocumentResponse,
  SavingsAccountInfoType,
  WidgetNames,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { DragScrollComponent } from 'ngx-drag-scroll';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-common-carousel',
  templateUrl: './common-carousel.component.html',
  styleUrls: ['./common-carousel.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CommonCarouselComponent {
  @Input() set accountsData(accountsData: (AccountInfoType | SavingsAccountInfoType)[]) {
    this.carouselData = accountsData;
    this.activeIndex = 0;
  }
  @Input() set beneficiaryData(beneficiaryData: GetBeneficiaryResponse[]) {
    this.carouselData = beneficiaryData;
    this.activeIndex = 0;
  }
  @Input() set banksData(banksData: SupportedInstitution[]) {
    this.carouselData = banksData;
    this.activeIndex = 0;
  }

  @Input() set documentsData(documentsData: FileDocumentResponse[]) {
    this.carouselData = documentsData;
    this.activeIndex = 0;
  }
  @ViewChild('commonCarousel', { read: DragScrollComponent })
  ds: DragScrollComponent;

  private activeIndex = 0;
  @Input() currencies: Currency[];
  @Input() countries: Country[];
  @Input() cardStyles: baseCssConfigStyle;
  @Input() templateFunction: HandlebarsTemplateDelegate<any>;
  @Input() baseHandleBarModel: any;
  @Input() widgetName = WidgetNames.BENEFICIARY_SELECTION_WIDGET;
  @Input() public carouselItemWidth: number | string = 93;
  @Input() public carouselItemWidthUnit = '%';
  @Input() public carouselItemHeight = 'auto';
  @Input() set activeAccountIndex(value: number) {
    this.activeIndex = value;
    setTimeout(() => {
      this.ds.moveTo(value);
    }, 0);
  }
  @Input() accountContext: AccountTypes | 'account';
  @Input() isDisabled = false;
  @Output() activeIndexUpdate = new EventEmitter<number>();
  @Output() itemClicked = new EventEmitter<any>();
  @Output() scrolled = new EventEmitter<void>();
  public carouselData: any[] = [];
  public uniqueId: string;

  public checkIndex(index: number) {
    this.activeIndex = index;
    this.activeIndexUpdate.emit(index);
    if (index === this.carouselData.length - 1) {
      this.scrolled.emit();
    }
  }

  /**
   * Invoked by `HorizontalUserScrollDirective` once the user has scrolled past
   * the near-end threshold. Re-emits on the component's public `scrolled`
   * output so existing consumers stay unchanged.
   */
  public onNearEndReached() {
    this.scrolled.emit();
  }

  public createTemplateData(
    data: AccountInfoType | GetBeneficiaryResponse | SupportedInstitution | FileDocumentResponse,
    index: number,
  ) {
    if (this.widgetName === WidgetNames.ACCOUNT_LIST_WIDGET) {
      return this.renderAccountsListingData(data as AccountInfoType, index);
    }
    if (this.widgetName === WidgetNames.BENEFICIARY_SELECTION_WIDGET) {
      return this.renderBeneficiaryItem(data as GetBeneficiaryResponse);
    }
    if (this.widgetName === WidgetNames.BANK_LISTING_WIDGET) {
      return this.renderBankItemTemplate(data as SupportedInstitution, index);
    }
    if (this.widgetName === WidgetNames.DOCUMENT_LISTING_WIDGET) {
      return this.renderDocumentItemTemplate(data as FileDocumentResponse, index);
    }

    return '';
  }

  private renderAccountsListingData(
    accountInfo: AccountInfoType | SavingsAccountInfoType,
    index: number,
  ): string {
    switch (this.accountContext) {
      case AccountTypes.CURRENT_ACCOUNT:
        return this.createAccountTemplateData(accountInfo as AccountInfoType, index);
      case AccountTypes.SAVINGS_ACCOUNT:
        return this.createSavingsAccountTemplateData(accountInfo as SavingsAccountInfoType, index);
      case 'account':
        return this.createSavingsAccountTemplateData(accountInfo as SavingsAccountInfoType, index);
      default:
        return '';
    }
  }

  public renderBeneficiaryItem(beneficiaryInfo: GetBeneficiaryResponse): string {
    return this.templateFunction({
      ...beneficiaryInfo,
      currencyCode: beneficiaryInfo.destinationIdentifier.currency,
      currencyName: getCurrenciesDataByKey(
        'currency_name',
        beneficiaryInfo.destinationIdentifier.currency,
        this.currencies,
      ),
    });
  }

  public renderDocumentItemTemplate(documentData: FileDocumentResponse, index: number) {
    return this.templateFunction({
      ...this.baseHandleBarModel,
      index,
      lastItem: index === this.carouselData.length - 1,
      document: {
        ...documentData,
      },
    });
  }

  public renderBankItemTemplate(supportedInstitution: SupportedInstitution, index: number) {
    return this.templateFunction({
      ...this.baseHandleBarModel,
      index,
      lastItem: index === this.carouselData.length - 1,
      bankName: supportedInstitution.name,
      bankBic: supportedInstitution.bic,
      bankLogo: supportedInstitution.logo,
      maxAccessValidForDays: supportedInstitution.max_access_valid_for_days,
      maxAccessValidForDaysReconfirmation: supportedInstitution.max_access_valid_for_days_reconfirmation,
      transactionTotalDays: supportedInstitution.transaction_total_days,
    });
  }

  private createSavingsAccountTemplateData(accountInfo: SavingsAccountInfoType, index: number) {
    const identifierFields = resolveAccountIdentifierFields(accountInfo, undefined);
    return this.templateFunction({
      ...this.baseHandleBarModel,
      index,
      accountInfo: accountInfo,
      lastItem: index === this.carouselData.length - 1,
      accountCurrency: accountInfo.currency,
      accountBalance: accountInfo.balance,
      indicativeBalance: accountInfo.balance,
      availableBalance: accountInfo.availableBalance,
      alias: accountInfo?.accountName ?? '',
      accountNumber: identifierFields.accountNumber,
      sortCode: identifierFields.sortCode ? addDashesToSortCode(identifierFields.sortCode) : undefined,
      bic: identifierFields.bic,
      ledgerNumber: identifierFields.ledgerNumber,
      iban: identifierFields.iban,
      providerExtraInfo: identifierFields.providerExtraInfo,
      achAccountNumber: identifierFields.achAccountNumber,
      achNumber: identifierFields.achNumber,
      abaAccountNumber: identifierFields.abaAccountNumber,
      abaNumber: identifierFields.abaNumber,
      accountProviderExtraInfo: identifierFields.providerExtraInfo,
      defaultMarker: index === 0 ? '(Default)' : '',
      currencyName: getCurrencyDataByKey('currency_name', accountInfo, this.currencies),
      countryName: getCountryNameByCountryCode(accountInfo.country, this.countries),
      accountStatus: `${accountInfo?.status?.charAt(0)?.toUpperCase()}${accountInfo?.status?.slice(1)}`,
    });
  }

  private createAccountTemplateData(accountInfo: AccountInfoType, index: number) {
    const identifierFields = resolveAccountIdentifierFields(accountInfo, undefined);
    return this.templateFunction({
      ...this.baseHandleBarModel,
      index,
      accountInfo: accountInfo,
      lastItem: index === this.carouselData.length - 1,
      accountCurrency: accountInfo.currency,
      accountBalance: accountInfo.balance,
      indicativeBalance: accountInfo.indicativeBalance,
      availableBalance: accountInfo.availableBalance,
      ledgerBalance: accountInfo?.ledgerBalance ?? '',
      ledgerBalanceIncludingPendingExchange: accountInfo?.ledgerBalanceIncludingPendingExchange ?? '',
      alias: accountInfo?.alias ?? '',
      accountNumber: identifierFields.accountNumber,
      sortCode: identifierFields.sortCode ? addDashesToSortCode(identifierFields.sortCode) : undefined,
      bic: identifierFields.bic,
      ledgerNumber: identifierFields.ledgerNumber,
      iban: identifierFields.iban,
      providerExtraInfo: identifierFields.providerExtraInfo,
      achAccountNumber: identifierFields.achAccountNumber,
      achNumber: identifierFields.achNumber,
      abaAccountNumber: identifierFields.abaAccountNumber,
      abaNumber: identifierFields.abaNumber,
      accountProviderExtraInfo: identifierFields.providerExtraInfo,
      pendingExchangeNetTotal: accountInfo?.scheduledExchanges?.netTotal ?? '',
      pendingExchangeCount: accountInfo?.scheduledExchanges?.totalBooked ?? '',
      pendingTransactionNetTotal: accountInfo?.scheduledPayments?.netTotal ?? '',
      pendingTransactionCount: accountInfo?.scheduledPayments?.totalBooked ?? '',
      defaultMarker: index === 0 ? '(Default)' : '',
      currencyName: getCurrencyDataByKey('currency_name', accountInfo, this.currencies),
      countryName: getCountryNameByCountryCode(accountInfo.country, this.countries),
      accountStatus: `${accountInfo?.status?.charAt(0)?.toUpperCase()}${accountInfo?.status?.slice(1)}`,
    });
  }

  public viewDetails(item: AccountInfoType | GetBeneficiaryResponse | FileDocumentResponse) {
    if ('accountId' in item) {
      this.uniqueId = item?.accountId;
    } else {
      this.uniqueId = item?.id;
    }
    this.itemClicked.emit(item);
  }
}
